import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Building2, Save, Loader2, CheckCircle2, AlertTriangle,
  Upload, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { useAdminOtpGuard } from './useAdminOtpGuard';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function resolveAssetUrl(url) {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${BACKEND}${url}`;
}

/**
 * SuperAdmin — Rupiwise Company Profile (seller identity for every
 * licence-purchase invoice we generate for a buyer). Phase 1 of the
 * Invoicing rollout: the numbers stored here drive the CGST/SGST vs
 * IGST decision, invoice-number prefix, and PDF letterhead.
 */
export function AdminBillingProfilePage() {
  const { admin, loading } = useAdminAuth();
  const [profile, setProfile] = useState(null);
  const [stateCodes, setStateCodes] = useState({});
  const [isConfigured, setIsConfigured] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editable form
  const [form, setForm] = useState({});

  const { guard, dialog } = useAdminOtpGuard();

  const refresh = async () => {
    try {
      const r = await adminApi.get('/admin/billing-profile');
      setProfile(r.data.profile);
      setStateCodes(r.data.state_codes || {});
      setIsConfigured(r.data.is_configured);
      setForm(r.data.profile || {});
    } catch {
      toast.error('Could not load billing profile');
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => { if (admin) refresh(); }, [admin]);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((p) => ({ ...p, [k]: v }));
    // Auto-fill state from state_code (or vice versa) as the user types
    if (k === 'state_code' && v && stateCodes[v]) {
      setForm((p) => ({ ...p, state_code: v, state: stateCodes[v] }));
    }
  };

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const handleSave = async (e) => {
    e.preventDefault();
    // Payload = only fields the user actually touched (or that changed).
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== (profile?.[k] ?? '')) payload[k] = v;
    });
    if (Object.keys(payload).length === 0) {
      toast.info('No changes to save');
      return;
    }
    await guard({
      requestOtpUrl: '/admin/billing-profile/request-otp',
      title: 'Confirm billing profile save',
      description: 'Editing the seller identity used on every generated invoice needs dual Email + WhatsApp OTP verification.',
      performSave: async (headers) => {
        setSaving(true);
        try {
          await adminApi.put('/admin/billing-profile', payload, { headers });
          toast.success('Billing profile saved');
          await refresh();
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const stateCodeOptions = useMemo(
    () => Object.entries(stateCodes).sort(([a], [b]) => a.localeCompare(b)),
    [stateCodes],
  );

  return (
    <div
      data-testid="admin-billing-profile-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800" data-testid="billing-profile-back">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Building2 className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <div className="text-white font-semibold flex items-center gap-2">
              Rupiwise Company Profile
              {isConfigured ? (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                  <CheckCircle2 className="inline w-2.5 h-2.5 mr-1" /> Ready
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                  <AlertTriangle className="inline w-2.5 h-2.5 mr-1" /> Setup required
                </span>
              )}
            </div>
            <div className="text-xs text-slate-400">Seller identity for every licence-purchase invoice</div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {fetching ? (
          <div className="flex items-center gap-2 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <form
            onSubmit={handleSave}
            data-testid="billing-profile-form"
            className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-6"
          >
            {/* -------- Identity -------- */}
            <section className="space-y-4">
              <h2 className="text-white font-semibold text-sm uppercase tracking-wider">Identity</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Legal name (as on PAN)"        value={form.legal_name}  onChange={set('legal_name')}  testId="bp-legal-name" required />
                <Field label="Trade / Brand name"            value={form.trade_name}  onChange={set('trade_name')}  testId="bp-trade-name" />
                <Field label="GSTIN (15 chars)"              value={form.gstin}       onChange={set('gstin')}       testId="bp-gstin" mono required />
                <Field label="PAN (10 chars)"                value={form.pan || ''}   onChange={set('pan')}         testId="bp-pan" mono />
              </div>
            </section>

            {/* -------- Address -------- */}
            <section className="space-y-4">
              <h2 className="text-white font-semibold text-sm uppercase tracking-wider">Registered Address</h2>
              <Field label="Address line" value={form.registered_address} onChange={set('registered_address')} testId="bp-address" multiline required />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="City" value={form.city} onChange={set('city')} testId="bp-city" />
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">State (GST code)</Label>
                  <select
                    data-testid="bp-state-code"
                    value={form.state_code || ''}
                    onChange={set('state_code')}
                    className="mt-1 w-full h-10 rounded-md bg-slate-800/50 border border-slate-700 text-white text-sm px-3"
                  >
                    <option value="">— Select —</option>
                    {stateCodeOptions.map(([code, name]) => (
                      <option key={code} value={code}>{code} · {name}</option>
                    ))}
                  </select>
                </div>
                <Field label="Pincode" value={form.pincode} onChange={set('pincode')} testId="bp-pincode" />
              </div>
              <Field label="Billing address (if different)" value={form.billing_address || ''} onChange={set('billing_address')} testId="bp-billing-address" multiline />
            </section>

            {/* -------- Contact -------- */}
            <section className="space-y-4">
              <h2 className="text-white font-semibold text-sm uppercase tracking-wider">Contact</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Phone"                 value={form.contact_phone}          onChange={set('contact_phone')}    testId="bp-phone" required />
                <Field label="Email (invoice From:)" value={form.contact_email}          onChange={set('contact_email')}    testId="bp-email" required type="email" />
                <Field label="Reply-to email"        value={form.invoicing_email || ''}  onChange={set('invoicing_email')}  testId="bp-reply-to" type="email" />
                <Field label="Website"               value={form.website || ''}          onChange={set('website')}          testId="bp-website" />
              </div>
            </section>

            {/* -------- Invoicing defaults -------- */}
            <section className="space-y-4">
              <h2 className="text-white font-semibold text-sm uppercase tracking-wider">Invoicing defaults</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="HSN / SAC" value={form.default_hsn_sac} onChange={set('default_hsn_sac')} testId="bp-hsn" mono />
                <Field label="Default GST %" value={form.default_gst_rate_percent} onChange={set('default_gst_rate_percent')} testId="bp-rate" mono type="number" />
                <Field label="Invoice number prefix" value={form.invoice_number_prefix} onChange={set('invoice_number_prefix')} testId="bp-prefix" mono />
              </div>
              <div className="flex items-center justify-between bg-slate-800/40 border border-slate-700 rounded-lg p-3">
                <div>
                  <div className="text-white text-sm font-medium">Prices include GST</div>
                  <div className="text-xs text-slate-400">
                    When ON, ₹2499 / ₹4499 are treated as GST-inclusive — the tax engine reverse-splits base + GST out of the amount Razorpay charged.
                  </div>
                </div>
                <Switch
                  checked={!!form.price_is_gst_inclusive}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, price_is_gst_inclusive: v }))}
                  data-testid="bp-inclusive-switch"
                />
              </div>
            </section>

            {/* -------- Signature (optional PDF stamp) -------- */}
            <section className="space-y-4">
              <h2 className="text-white font-semibold text-sm uppercase tracking-wider">Authorised signatory</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Signatory name" value={form.signatory_name || ''} onChange={set('signatory_name')} testId="bp-signatory-name" />
                <SignatureUploader
                  value={form.signature_image_url}
                  onUploaded={(url) => setForm((p) => ({ ...p, signature_image_url: url }))}
                />
              </div>
              <p className="text-[11px] text-slate-500">
                When set, the image is stacked above the "Authorised Signatory" line on every generated invoice + credit-note PDF. PNG with a transparent background looks best; ≤ 500 KB.
              </p>
            </section>

            {/* -------- Bank (optional footer) -------- */}
            <section className="space-y-4">
              <h2 className="text-white font-semibold text-sm uppercase tracking-wider">Bank details (optional PDF footer)</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Bank name"       value={form.bank_name || ''}          onChange={set('bank_name')}          testId="bp-bank-name" />
                <Field label="Account number"  value={form.bank_account_number || ''} onChange={set('bank_account_number')} testId="bp-bank-acct" mono />
                <Field label="IFSC"            value={form.bank_ifsc || ''}          onChange={set('bank_ifsc')}          testId="bp-ifsc" mono />
              </div>
            </section>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="submit"
                disabled={saving}
                data-testid="billing-profile-save"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> Save profile</>
                )}
              </Button>
            </div>
          </form>
        )}
      </main>
      {dialog}
    </div>
  );
}


function Field({ label, value, onChange, testId, mono, multiline, type = 'text', required }) {
  const cls = `bg-slate-800/50 border-slate-700 text-white ${mono ? 'font-mono text-sm' : ''}`;
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-300 text-xs uppercase tracking-wider">
        {label}{required && <span className="text-amber-400 ml-0.5">*</span>}
      </Label>
      {multiline ? (
        <Textarea value={value ?? ''} onChange={onChange} data-testid={testId} className={cls} rows={2} required={required} />
      ) : (
        <Input value={value ?? ''} onChange={onChange} data-testid={testId} className={cls} type={type} required={required} />
      )}
    </div>
  );
}


function SignatureUploader({ value, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const MAX_BYTES = 500 * 1024;

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Signature must be an image (PNG, JPEG, SVG, WebP)');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Signature exceeds ${MAX_BYTES / 1024} KB limit`);
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const res = await adminApi.post('/admin/billing-profile/upload-signature', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(res.data.url);
      toast.success('Signature uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const clear = async () => {
    try {
      await adminApi.delete('/admin/billing-profile/signature');
      onUploaded('');
      toast.success('Signature removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remove failed');
    }
  };

  return (
    <div data-testid="bp-signature-uploader" className="space-y-1.5">
      <Label className="text-slate-300 text-xs uppercase tracking-wider">Signature image</Label>
      <div className="flex items-center gap-3">
        <div className="w-28 h-14 rounded-md bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden shrink-0" data-testid="bp-signature-preview">
          {value ? (
            <img src={resolveAssetUrl(value)} alt="Signature" className="max-w-full max-h-full object-contain" />
          ) : (
            <ImageIcon className="w-5 h-5 text-slate-600" />
          )}
        </div>
        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-slate-200 text-sm hover:bg-slate-700">
          {uploading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
          ) : (
            <><Upload className="w-4 h-4" /> Choose file</>
          )}
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; upload(f); }}
            className="hidden"
            data-testid="bp-signature-file-input"
            disabled={uploading}
          />
        </label>
        {value && (
          <button
            type="button"
            onClick={clear}
            data-testid="bp-signature-remove"
            className="text-xs text-slate-400 hover:text-red-400"
          >
            Remove
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500">
        PNG with transparent background recommended · ≤ 500 KB.
      </p>
    </div>
  );
}
